import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLive } from '../live/LiveContext';
import api, { encodeId } from '../api/client';
import { MEETINGS_ROUTE } from '../constants';
import { hasFeatureAccess, useAuth } from '../context/AuthContext';
import { etDateKey, etWallToUTC, formatWallTimeET } from '../meetingTime';

const WINDOW_MS = 8 * 60 * 60 * 1000;
const DISMISS_KEY = 'upcoming-interviews-dismissed';
const POLL_MS = 60 * 1000;
const FOCUS_THROTTLE_MS = 30 * 1000;

function isCancelled(meeting) {
  const name = String(meeting?.status_name || '').toLowerCase();
  return name.includes('cancel');
}

function startsWithinWindow(meeting, nowMs) {
  const utc = etWallToUTC(meeting?.scheduled_at_et || '');
  if (!utc) return false;
  const start = new Date(utc).getTime();
  if (!Number.isFinite(start)) return false;
  const durationMin = Number(meeting?.duration_minutes);
  const durationMs = Number.isFinite(durationMin) && durationMin > 0
    ? durationMin * 60 * 1000
    : 0;
  const end = start + durationMs;
  const upcoming = start >= nowMs && start <= nowMs + WINDOW_MS;
  const inProgress = start <= nowMs && nowMs < end;
  return upcoming || inProgress;
}

function leadLabel(meeting, nowMs) {
  const start = new Date(etWallToUTC(meeting.scheduled_at_et)).getTime();
  const diffMin = Math.round((start - nowMs) / 60000);
  if (diffMin <= 0) return 'In progress';
  if (diffMin < 60) return `in ${diffMin} min`;
  const hours = Math.floor(diffMin / 60);
  const mins = diffMin % 60;
  return mins ? `in ${hours}h ${mins}m` : `in ${hours}h`;
}

function fingerprint(meetings) {
  return meetings.map((m) => String(m.id)).sort().join(',');
}

function UpcomingInterviewsNotice() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState('');
  const lastFetchRef = useRef(0);

  // Live: the banner follows interviews added or moved anywhere.
  const loadRef = useRef(null);
  useLive(['meetings'], () => loadRef.current && loadRef.current(), { delay: 1500 });

  const load = useCallback(async () => {
    if (!hasFeatureAccess(user, 'interviews') && !hasFeatureAccess(user, 'calendar')) {
      setItems([]);
      return;
    }
    lastFetchRef.current = Date.now();
    try {
      const res = await api.get('/api/meetings', {
        params: {
          start_date: etDateKey(0),
          end_date: etDateKey(1),
          sort: 'scheduled_at_asc',
          limit: 200,
          compact: 1,
        },
      });
      const now = Date.now();
      const next = (res.data.meetings || [])
        .filter((meeting) => !isCancelled(meeting) && startsWithinWindow(meeting, now))
        .sort((a, b) => String(a.scheduled_at_et).localeCompare(String(b.scheduled_at_et)));
      setItems(next);
    } catch {
      setItems([]);
    }
  }, [user]);
  loadRef.current = load;

  useEffect(() => {
    load();
    const isVisible = () => document.visibilityState === 'visible';
    // Hidden tabs skip the poll; they catch up when they become visible again.
    const timer = window.setInterval(() => {
      if (isVisible()) load();
    }, POLL_MS);
    const refreshIfStale = () => {
      if (!isVisible()) return;
      if (Date.now() - lastFetchRef.current < FOCUS_THROTTLE_MS) return;
      load();
    };
    window.addEventListener('focus', refreshIfStale);
    document.addEventListener('visibilitychange', refreshIfStale);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshIfStale);
      document.removeEventListener('visibilitychange', refreshIfStale);
    };
  }, [load]);

  useEffect(() => {
    try {
      setDismissed(window.sessionStorage.getItem(DISMISS_KEY) || '');
    } catch {
      setDismissed('');
    }
  }, []);

  const key = fingerprint(items);
  if (!items.length || dismissed === key) return null;

  const dismiss = () => {
    setDismissed(key);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, key);
    } catch {
      // The banner still hides for this view if storage is blocked.
    }
  };

  const now = Date.now();

  return (
    <section className="upcoming-interviews" aria-live="polite">
      <div className="upcoming-interviews-head">
        <strong>
          {items.length === 1 ? '1 interview' : `${items.length} interviews`}
          {' '}
          in the next 8 hours
        </strong>
        <button type="button" className="upcoming-interviews-dismiss" onClick={dismiss}>
          Dismiss
        </button>
      </div>
      <ul className="upcoming-interviews-list">
        {items.map((meeting) => (
          <li key={meeting.id}>
            <Link to={`${MEETINGS_ROUTE.path}/${encodeId(meeting.id)}`}>
              <span className="upcoming-interviews-when">{leadLabel(meeting, now)}</span>
              <span className="upcoming-interviews-who">
                {meeting.profile_name || 'Candidate'}
                {meeting.company ? ` · ${meeting.company}` : ''}
                {meeting.role ? ` · ${meeting.role}` : ''}
              </span>
              <span className="upcoming-interviews-clock">
                {formatWallTimeET(meeting.scheduled_at_et)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default UpcomingInterviewsNotice;
